import { addMinutes, format } from "date-fns"
import { formatInTimeZone } from "date-fns-tz"
import { useCallback } from "react"
import { useSelector } from "react-redux"
import { getUserProfile } from "@/reducers/userProfile"

type FormatParameters = Parameters<typeof format>

interface UserTimeFormat {
  (date: string | Date | number, formatStr: string, options?: FormatParameters[2]) : string
}

// User Preferred Timezone handling:
// 2025-10-20
// Since user can choose his preferred timezone on his profile page, and this chosen timezone
// may not be the same with the current browser timezone, our portal app needs to handle the mismatch.
// There are 2 different approaches:
//  - store dates with brower timezone, present with user timezone adaptation           # 3rd party component might not support user timezone
//  - convert every date into user timezone, then store and display the converted dates # may be hard to control every date input/creation
// We have chosen the first option, trying to add an adaptation layer to any imcompatible 3rd party components.
//

export const useUserTimeFormat = (): UserTimeFormat => {
  const user = useSelector(getUserProfile)

  const formatWrapped : UserTimeFormat = useCallback((date, formatStr, options?) => {
    if (user?.ianaTimeZone) {
      return formatInTimeZone(new Date(date), user.ianaTimeZone, formatStr)
    }
    const browserOffset = -(new Date().getTimezoneOffset())
    // every Date instance is implicitly in browser timezone 
    const dt = buildDateTime(new Date(date), browserOffset)

    if (user?.timeZoneOffset !== undefined) {
      dt.shiftToTimezone(user.timeZoneOffset)
    }
    const d = dt.getDate()
    return format(d, formatStr, options)
  }, [user?.ianaTimeZone, user?.timeZoneOffset])

  return formatWrapped
}

export function buildDateTime(date:Date, timezoneOffset:number) {
  let d = date
  let offset = timezoneOffset

  const shiftToTimezone = (targetTimezoneOffset:number) => {
    d = addMinutes(d, - offset + targetTimezoneOffset)
    offset = targetTimezoneOffset
  }

  const elapse = (minutes:number) => {
    d = addMinutes(d, minutes)
  }

  const toISOString = () => {
    const offset0 = offset
    shiftToTimezone(-(new Date().getTimezoneOffset()))
    const d1 = d
    shiftToTimezone(offset0)
    return d1.toISOString()
  }

  const getDate = () => new Date(d)
  const getTimezoneOffset = () => offset

  return {
    shiftToTimezone,
    elapse,
    toISOString,
    getDate,
    getTimezoneOffset,
  }
}


